import {Map} from "ol";
import {Layer} from "ol/layer";
import {getDistance} from "ol/sphere";
import {fromLonLat, toLonLat} from "ol/proj";

import {defaults} from "olgm/interaction.js";

import global from "../global.js";
import OpenLayersDriverGoogle from "./OpenLayersDriverGoogle.js";
import OpenLayersDriverOSM from "./OpenLayersDriverOSM.js";

export const GIS_TYPE_GOOGLE_MAPS = 1,
    GIS_TYPE_OSM = 2,
    GIS_TYPE_ESRI_2D = 3;

const log = global.log.getLogger("f_mtac.view.OpenLayersDriver");
log.setLevel(log.levels.WARN);

/**
 * The OpenLayersDriver class is responsible for:
 *  - loading google map
 *  - interactions with Tacteam View class zoom and translations
 * The googlemap is embedded in an OpenLayers layer
 */
const OpenLayersDriver = function (view_, type, GoogleLayer, OLGoogleMaps) {
    log.debug("OpenLayersDriver", {view_, type, GoogleLayer, OLGoogleMaps});

    var view = view_;
    var olMap; // OpenLayers Map module (see OpenLayers API)
    var olCustomerDriver = null; // Instanciate OpenLayersDriverGoogle/OSM/ESRI2D...
    var olBasicLayer = null; // Basic Layer Gis (ex olGM for Google Maps)
    let current_viewState = {}; // OL current view state
    let initial_viewState = {}; // OL view state when area selected
    let initial_size = {}; // map size when area selected
    let initial_zoom = {}; // map zoom when area selected (useful for gis cache)
    let initial_extent = {}; // view extent when area selected (save because virtual keyboard disturb the value)
    let initial_tiles_array = [];
    let current_size = {}; // size of the map
    let geocoding_address = ""; // last address of geocoding feature

    const min_zoom = 1; // min authorized zoom value
    let mapBasicLayerType = ""; // ex : google.maps.MapTypeId, OSM bsic layer type,... ;
    let zoom_ongoing = false;
    let is_removing = false; // true if map is in the removing process or removed, to prevent unexpected display effect
    let export_ongoing = false; // used to know if the export must be launched after centering (and so after map rendering)
    let sitac_creation_ongoing = false;
    /**
     * All properties
     */
    Object.defineProperties(this, {
        isActive: {
            // true if the map is displayed
            set: () => {},
            get: () => olMap !== null && olMap !== undefined
        },
        width: {
            set: () => {},
            get: () => initial_size[0]
        },
        height: {
            set: () => {},
            get: () => initial_size[1]
        },
        ol_map: {
            set: () => {},
            get: () => olMap
        },
        ol_basic_layer: {
            set: (value) => {
                olBasicLayer = value;
            },
            get: () => olBasicLayer
        },
        initial_viewState: {
            set: () => {},
            get: () => initial_viewState
        },
        initial_size: {
            set: () => {},
            get: () => initial_size
        },
        initial_zoom: {
            set: () => {},
            get: () => initial_zoom
        },
        initial_extent: {
            set: () => {},
            get: () => initial_extent
        },
        initial_tiles_array: {
            set: () => {},
            get: () => initial_tiles_array
        },
        current_viewState: {
            set: () => {},
            get: () => current_viewState
        },
        current_size: {
            set: () => {},
            get: () => current_size
        },
        is_removing: {
            set: (value) => {
                is_removing = value;
            },
            get: () => is_removing
        },
        zoom_ongoing: {
            set: () => {},
            get: () => zoom_ongoing
        },
        sitac_creation_ongoing: {
            set: (value) => {
                sitac_creation_ongoing = value;
            },
            get: () => sitac_creation_ongoing
        },
        export_ongoing: {
            set: (value) => {
                export_ongoing = value;
            },
            get: () => export_ongoing
        },
        mapBasicLayerType: {
            set: (value) => {
                log.debug("set mapBasicLayerType", {value});
                mapBasicLayerType = value;
            },
            get: () => mapBasicLayerType
        },
        ol_customer_driver: {
            set: () => {},
            get: () => olCustomerDriver
        }
    });

    /**
     * @brief Compute and set the resource scale (size of resource icons)
     * depenging on the OL zoom level.
     * When zoom is low, the size is minimum (value from configuration) and the pictures and names are hidden
     * When zoom is intermediate, the size is on a slope between the minimum and the maximum
     * When zoom is high, the size is set to the one setted from the menu
     */
    this.update_resource_scale = () => {
        let min = global.default_configuration.gis.resource.size_min / 100.0;
        let max = global.resources.scale_setting;
        if (global.default_configuration.gis.resource.zoom_max - global.default_configuration.gis.resource.zoom_min > 0 && min > 0) {
            let slope = (max - min) / (global.default_configuration.gis.resource.zoom_max - global.default_configuration.gis.resource.zoom_min);
            let zoom = olMap.getView().getZoom();
            if (zoom <= global.default_configuration.gis.resource.zoom_min) {
                global.resources.scale = min;
                // hide pictures and names, keep only the color circles
                global.resources.hide_pictures();
            } else if (zoom < global.default_configuration.gis.resource.zoom_max) {
                global.resources.scale = min + (zoom - global.default_configuration.gis.resource.zoom_min) * slope;
                global.resources.show_pictures();
            } else {
                /* >= 20 */
                global.resources.scale = max;
                global.resources.show_pictures();
            }
            log.debug(`compute_and_launch_transform zoom = ${zoom} slope = ${slope} scale_setting = ${global.resources.scale_setting} scale = ${global.resources.scale}`);
        } else {
            // use directely the menu setting
            global.resources.scale = global.resources.scale_setting;
        }
    };

    var compute_and_launch_transform = () => {
        // The geometric operation is an homotety with change of center and change of zoom
        // current_viewState.center can be undefined if the client gets a shared GIS
        if (initial_viewState.center !== undefined && current_viewState.center !== undefined && is_removing == false) {
            // translation of the center in SVG pixels
            let dx_center = (current_viewState.center[0] - initial_viewState.center[0]) / current_viewState.resolution;
            let dy_center = (current_viewState.center[1] - initial_viewState.center[1]) / current_viewState.resolution;
            // calculate zoom rate
            // dont use viewState.zoom : it is a kind logarithm of the zoom level, do use resolution

            /*let power = olMap.getView().getZoom() - 16;
            let k = Math.pow(2, power);
            log.debug(`OpenLayersDriver.compute_and_launch_transform power = ${power}, k = ${k}`);
            */
            let k = initial_viewState.resolution / current_viewState.resolution;

            // translation based on the zoom point (position of the mouse when zooming)
            let sizex = initial_size[0];
            let sizey = initial_size[1];
            let dx_zoom = ((1 - k) * sizex) / 2.0;
            let dy_zoom = ((1 - k) * sizey) / 2.0;

            // translation based on window resize : the center doesn't move
            let dx_resize = (current_size[0] - initial_size[0]) / 2.0;
            let dy_resize = (current_size[1] - initial_size[1]) / 2.0;
            //console.log("resize", {dx_resize, dy_resize});

            // combine translations
            let dx = dx_center - dx_zoom - dx_resize;
            let dy = dy_center + dy_zoom + dy_resize;

            // Apply the transform in the SVG  view
            global.view.launch_transform(-dx, dy, k, current_viewState.center, current_viewState.resolution);

            // Adapt the resource scale (size of icon resource) depending on the zoom level
            this.update_resource_scale();

            // AGPOC-8535 : export for GIS must wait for OL/D3 conversion in order to well rendered map and d3 layers (grid and objects) before exporting
            if (export_ongoing) {
                global.scene.export_map();
            }
        }
    };

    /**
     * Public method for compute_and_launch_transform()
     */
    this.launch_transform = function () {
        compute_and_launch_transform();
    };

    /**
     * function to calculate new map layer resolution according to d3 zoom level (called in offline mode)
     * current_viewstate is still modified event in offline mode? So no need to recalculate it
     */
    var compute_gis_context = async () => {
        log.debug("compute_gis_context ", global.storage.selected_context.data.gis);
        if (global.storage.selected_context.data.gis === undefined) {
            return;
        }
        // The geometric operation is an homotety with change of center and change of zoom
        // current_viewState.center can be undefined if the client gets a shared GIS
        if (initial_viewState.center !== undefined && current_viewState.center !== undefined) {
            //let x = global.storage.selected_context.data.transform.x;
            //let y = global.storage.selected_context.data.transform.y;
            let k = global.storage.selected_context.data.transform.k;
            // calculate new resolution
            let new_resolution = initial_viewState.resolution / k;

            // update selected_context gis data
            global.storage.selected_context.data.gis.resolution = new_resolution;
            let center = [current_viewState.center[0], current_viewState.center[1]];
            global.storage.selected_context.data.gis.center = center;
        }
    };

    /**
     * Public method for compute_gis_context()
     */
    this.compute_gis_offline = function () {
        compute_gis_context();
    };

    /**
     * Creates an Openlayers map
     * Creates an Openlayers layer with googlemap
     * Initiates Geocoding
     * When a map is defined in the background, the zoom and translations,
     *   events are got from Openlayers and a transformation is calculated
     *   and applied to SVG components (see Layer:render)
     * @returns {HTMLElement}
     */
    this.activate = function () {
        log.debug("activate IN");

        olMap = new Map({
            projection: "EPSG:3857",
            //projection: "EPSG:4326",
            controls: [], // no rotate nor zoom controls
            target: "map",
            interactions: defaults()
        });

        const layer = new Layer({
            // when a tranformation is detected on the OpenLayers layer,
            // compute and apply the transformation on D3/SVG side
            /**
             * Get the zoom and translation information,
             * Computes the SVG transform (translate + scale)
             * Apply the transform to SVG
             * @param frameState: viewState.center and viewState.resolution are used
             * @returns {HTMLElement}
             */
            render(frameState) {
                log.debug("frameState ", frameState);
                current_viewState = frameState.viewState;
                current_size = frameState.size;
                if (initial_viewState.center !== undefined && zoom_ongoing == false) {
                    // compute and launch when translations, not when zooming
                    compute_and_launch_transform();
                }
                // AGPOC-9892 : OSM static map
                // zoom is rounded before creating gis map
                if (sitac_creation_ongoing) {
                    initial_viewState = current_viewState;
                    global.scene.map.map_gis.continue_initial_frame_creation();
                    sitac_creation_ongoing = false;
                }
                // when in offline mode, update and save new resolution and center so that they will be recovered when changing context and when coming back to online state
                if (global.server.network.state === "offline") {
                    compute_gis_context();
                }
                return document.getElementById("#map_area");
            }
        });
        olMap.addLayer(layer);

        // create specific OpenLayers Driver (Google, OSM, ...)
        // depends on configuration gis type
        switch (type) {
            case GIS_TYPE_OSM:
                olCustomerDriver = new OpenLayersDriverOSM(view, this);
                break;
            default:
                olCustomerDriver = new OpenLayersDriverGoogle(view, this);
                break;
        }
        // olCustomerDriver.activate();

        global.olmap = olMap; // used for debug
        log.debug("activate OUT");
    };

    /**
     * Center the view on a given resource
     * @param w width of the object in SVG coordinates
     * @param h height of the resource in SVG coordinates
     * @param x position of the resource left in SVG coordinates
     * @param y position of the resource top in SVG coordinates
     */
    this.center_to_object = function ({width: w, height: h, x, y}) {
        log.debug("center_to_object", {w, h, x, y, current_center: current_viewState.center});
        let olView = olMap.getView();
        if (olView != null) {
            // moving x,y to the center of the object
            x += w / 2;
            y += h / 2;

            // change from D3 (x,y) to OL (x,y) by applying resolution
            // dx_object and dy_object are the coordinates of the center of the object
            // from the top-left of the initial_viewState, and in OL coordinate system
            let dx_object = x * initial_viewState.resolution;
            let dy_object = y * initial_viewState.resolution;

            // dx_center and dy_center are the coordinates of the center of the view
            // from the top-left of the initial_viewState, and in OL coordinate system
            let dx_center = initial_viewState.center[0] - (initial_size[0] / 2) * initial_viewState.resolution;
            let dy_center = initial_viewState.center[1] + (initial_size[1] / 2) * initial_viewState.resolution;

            // combine translations
            let center = [dx_center + dx_object, dy_center - dy_object];

            log.debug("center_to_object", {center});
            olView.setCenter(center);
        }
    };

    /**
     * Add basic map layer according to configuration
     * @param type map Type, example maps.MapTypeId.ROADMAP
     * @returns {boolean} false in case of exception (map not loaded)
     */
    this.add_map_layer = async function (type) {
        if (olBasicLayer !== null) {
            log.debug("basic layer already added");
            return false;
        }
        if (olMap === null) this.activate();
        return await olCustomerDriver.add_map_layer(type);
    };

    /**
     * Remove the google map layer (when the space is closed)
     */
    this.remove_map_layer = function () {
        log.debug("remove_map_layer olBasicLayer ", olBasicLayer);
        if (olBasicLayer != null) {
            is_removing = true;
            olCustomerDriver.remove_map_layer();
        }
        olBasicLayer = null;
    };

    /**
     * set the fallback media id (used when offline or gis not authorized)
     */
    this.set_map_fallback_media = function (media_id, map_type, object) {
        return olCustomerDriver.set_map_fallback_media(media_id, map_type, object);
    };

    /**
     *  set the initial zoom and tiles in order to recover static map for others basic layers
     *  zoom and tiles saved in dataGis object are initial values only
     */
    this.set_initial_zoom_and_tiles = function (zoom, tiles) {
        log.debug("set_initial_zoom_and_tiles", {zoom, tiles});
        initial_zoom = zoom;
        initial_tiles_array = tiles;
    };

    /**
     * Get the last requested address
     * @returns {string}
     */
    this.get_geocoding_address = function () {
        log.debug("get_geocoding_address", {geocoding_address});
        return geocoding_address;
    };

    /**
     * Set the last requested address
     */
    this.set_geocoding_address = function (address) {
        log.debug("set_geocoding_address", address);
        if (address === null || address === undefined || address === "") {
            return;
        }
        log.debug("set_geocoding_address", address);
        geocoding_address = address;

        olCustomerDriver.remove_geocoding_address();
        olCustomerDriver.display_geocoding_address(address);
    };

    /**
     * call search address function of the activated openlayers driver
     */
    this.search_address = async function () {
        log.debug("search_address");
        let address = await olCustomerDriver.search_address();
        // address must include location (lat, lon) + name (can be different from GIS type to another)
        if (address.location !== null) {
            let center = fromLonLat(address.location);
            log.debug("geocoding", {center});
            global.view.save_geocoding_gis_context(address);
            this.set_center_and_zoom(center, 18);
            this.set_geocoding_address(address);
        }
    };

    /**
     * Apply the given frame state (center and resolution) to the displayed map
     * @param {object} dataGis containing the center, resolution, ... what is saved in indexedDB
     * @returns {boolean} true if success, false if error
     */
    this.set_initial_frame = function (dataGis) {
        log.debug("set_initial_frame", dataGis);
        if (olMap !== null) {
            if (olBasicLayer == null) {
                // set the map type before adding the google map layer
                let mapType = dataGis.mapType; // specific to google map layer
                log.debug("mapType ", mapType);
                //mapType = "roadmap";
                var ret = this.add_map_layer(mapType);
                if (ret == false) {
                    return false;
                }
            }
            initial_viewState.center = dataGis.viewState.center;
            initial_viewState.resolution = dataGis.viewState.resolution;
            initial_size = dataGis.size;
            initial_tiles_array = dataGis.tiles;
            initial_extent = dataGis.extent;
        }
        return true;
    };

    /**
     * Apply a given center and a given resolution to the GIS view
     * @param center [x,y] array of coordinates of the center
     * @param resolution
     */
    this.set_center_and_resolution = function (center, resolution) {
        log.debug("set_center_and_resolution", {center, resolution});
        if (olMap != null) {
            olMap.getView().setCenter(center);
            olMap.getView().setResolution(resolution);
        }
    };

    /**
     * Apply a given center and find the resolution so that 
     * the extent is all inside the screen
     * @param gis input object with viewstate and exent (from indexedDB storage.x.y)
     */
     this.init_viewstate_full_screen = function (gis) {
        log.debug("init_viewstate_full_screen", gis);
        if (olMap != null) {
            olMap.getView().setCenter(gis.viewState.center);
            olMap.getView().setResolution(gis.viewState.resolution);

            // loop on unzoom while the initial extent is not 
            // inside the current view
            let inside = false;
            while (inside === false) {
                let current_extent = olMap.getView().calculateExtent();
                log.debug("init_viewstate_full_screen", current_extent);
                if (gis.extent[0] < current_extent[0] // west
                    || gis.extent[1] < current_extent[1] // south
                    || gis.extent[2] > current_extent[2] // east
                    || gis.extent[3] > current_extent[3]) { // north
                        let current_zoom = olMap.getView().getZoom();
                        olMap.getView().setZoom(current_zoom - 1);
                }
                else {
                    // it is inside, get out the loop
                    inside = true;
                    break;
                }
            }
        }
    };

    /**
     *
     * @param center: array [x center,y center] with OL coordinates
     * @param zoom: OL zoom value
     */
    this.set_center_and_zoom = function (center, zoom) {
        log.debug("set_center_and_zoom", {center, zoom});
        if (olMap != null) {
            olMap.getView().setCenter(center);
            olMap.getView().setZoom(zoom);
        }
    };

    /**
     * Get initial map center and zoom
     * @returns {center: {longitude,latitude}, zoom: }
     */
    this.get_center_and_zoom = function () {
        log.debug("get_center_and_zoom");
        // default values for France
        let center = fromLonLat([2.2, 46.23]);
        let zoom = 6;
        if (olMap != null) {
            center = toLonLat(olMap.getView().getCenter());
            log.debug("center =", center);
            zoom = olMap.getView().getZoom();
            log.debug("zoom =", zoom);
        }
        return {center: center, zoom: zoom};
    };

    /**
     * @brief Convert a [x,y] point (OpenLayer point) to longitude latitude
     * @param point array of two values
     * @return array [lon, lat]
     */
    this.to_lon_lat = function (point) {
        let result = toLonLat(point);
        log.debug("to_lon_lat", {point, result});
        return result;
    };

    /**
     * @brief Convert a [longitude,latitude] coordinate to an [x,y] D3 point
     * @param coord [lon,lat]
     * @return array [x, y] in pixel (D3)
     */
    this.from_lon_lat_position_to_pixel = function (coord) {
        let ol = fromLonLat(coord);
        let pixel = this.getPixelCoordinates(ol);
        log.debug("from_lon_lat_position_to_pixel", {coord, pixel});
        return pixel;
    };

    /**
     * @brief Convert a [longitude,latitude] coordinate to an [x,y] point (depending on projection ~ meters near equator)
     * @param coord [lon,lat]
     * @return array [x, y] in OpenLayer projection (i.e meters near the equator)
     */
    this.from_lon_lat_to_ol = function (coord) {
        let ol = fromLonLat(coord);
        log.debug("from_lon_lat_to_ol", {coord, ol});
        return ol;
    };

    /**
     * Save initial map : save the center, the resolution and the window size
     * This information is used for rendering SVG display from OpenLayers state (see Layer.render())
     * @returns {{size: {}, viewState: {center, resolution}}}
     */
    this.save_initial_frame = function () {
        log.debug("trace save_initial_frame");
        if (olMap !== null) {
            initial_viewState = current_viewState;
            initial_size = current_size;
            initial_zoom = olMap.getView().getZoom();
            initial_extent = olMap.getView().calculateExtent();
            log.info("save_initial_frame", {initial_viewState, initial_size, initial_zoom, initial_extent});
        }
    };

    /**
     * Save initial tiles : save an array of tiles initial coordinates in order to get the static map (fallback media) corresponding to layer type if needed
     * @returns [{center: {}, pixel coordinates: {}}]
     * pixel coordinates are used to determine the place of the tile in the canvas
     * for OSM, as the returned tiles will not begin to pixel [0,0], we have to take some margin and ask for all tiles around the specified zone
     * tile_numbers is added of 2 for width and height
     */
    this.save_initial_tiles = function () {
        log.debug("trace save_initial_tiles");
        initial_tiles_array = [];
        if (olMap !== null) {
            let center = initial_viewState.center[1] + "," + initial_viewState.center[0];
            let map_size = {width: initial_size[0], height: initial_size[1]};
            let tile_size = olCustomerDriver.get_tile_size();
            let tile_numbers = [Math.ceil(map_size.width / tile_size) + 2, Math.ceil(map_size.height / tile_size) + 2];

            for (let i = 1; i <= tile_numbers[0]; i++) {
                for (let j = 1; j <= tile_numbers[1]; j++) {
                    var tile_center = global.view.ol_driver.ol_map.getCoordinateFromPixel([(i - 2) * tile_size + tile_size / 2, (j - 2) * tile_size + tile_size / 2]);
                    center = toLonLat(tile_center);
                    var coordinates = {
                        x: (i - 2) * tile_size,
                        y: (j - 2) * tile_size
                    };
                    initial_tiles_array.push({center, coordinates});
                }
            }
            log.debug("save_initial_tiles", {initial_tiles_array});
        }
    };

    /**
     * create static images tiles
     * @returns array of blob
     */
    this.static_map_create_blob_tiles = function (gis_state) {
        return olCustomerDriver.static_map_create_blob_tiles(gis_state);
    };

    /**
     * Get parameters of the frames initiated by the user
     * @returns {{size: {}, viewState: {center, resolution}}}
     */
    this.get_initial_frame = function () {
        if (olMap !== null) {
            var state = {
                viewState: {
                    center: initial_viewState.center,
                    resolution: initial_viewState.resolution
                },
                size: initial_size, // screen size
                mapType: mapBasicLayerType,
                // zoom and array of tiles added, used when rendering GIS cache
                zoom: initial_zoom,
                tiles: initial_tiles_array,
                extent: initial_extent
            };
            log.debug("get_initial_frame return ", {state});
            return state;
        }
    };

    /**
     * Switch map basic layer type
     */
    this.switch_map_basic_layer = function (fromAssistant) {
        log.debug("switch_map_type fromAssistant ", fromAssistant);

        if (olBasicLayer != null) {
            olCustomerDriver.switch_map_basic_layer(fromAssistant);
        }
    };

    /**
     * Apply a zoom after pressing on the zoom buttons. Check the max zoom.
     * @param {number} delta: delta zoom to apply to the current zoom (+1 or -1)
     */
    this.zoom = function (dk) {
        log.debug("zoom IN", {dk});
        if (olMap !== null) {
            let delta = 1;
            if (dk < 1) {
                delta = -1;
            }
            let olView = olMap.getView();
            let new_zoom = olView.getZoom() + delta;
            let max_zoom = olCustomerDriver.get_max_zoom();
            if (new_zoom < min_zoom || new_zoom > max_zoom) {
                return;
            }
            log.debug("zoom set ", {new_zoom});
            olView.setZoom(new_zoom);
        }
    };

    /**
     * @brief Calculates coordinates in the OL map, from a given pixel on the screen.
     *        The method olMap.getCoordinateFromPixel is not trusty !!
     * @param pixel, pixel in the screen
     * @param extent, coordinates of the screen extent
     * @param size, size of the screeen in pixel
     * @returns [X,Y] the coordinates in the OL map
     */
    let getOLCoordinates2 = (pixel, extent, size) => {
        //let size = olMap.getSize();
        //log.debug("getCoordinates size =  ", {size});
        //let extent = olMap.getView().calculateExtent(size);
        //log.debug("getCoordinates extent =  ", {extent});
        let coordinate = [extent[0] + ((extent[2] - extent[0]) * pixel[0]) / size[0], extent[1] + ((extent[3] - extent[1]) * (size[1] - pixel[1])) / size[1]];
        return coordinate;
    };

    /**
     * @brief Calculates coordinates in the OL map, from a given pixel on the screen.
     * @param pixel, [x,y] point in the screen (D3 coordinates)
     * @returns [X,Y] the coordinates in the OL map, meters near the equator
     */
    this.from_pixel_to_ol = (pixel) => {
        let state = this.get_initial_frame();

        return getOLCoordinates2(pixel, state.extent, state.size);
    };

    /**
     * @brief Calculates longitude, latitude in the OL map, from a given pixel on the screen.
     * @param pixel, [x,y] point in the screen (D3 coordinates)
     * @returns [lon,lat] the position in the OL map
     */
    this.from_pixel_position_to_lon_lat = (pixel) => {
        let state = this.get_initial_frame();

        let ol = getOLCoordinates2(pixel, state.extent, state.size);
        return toLonLat(ol);
    };

    /**
     * @brief Calculates size of the screen in OL coordinate system.
     * @returns [width,height] the size of the screen
     */
    this.getOLSize = () => {
        let state = this.get_initial_frame();
        log.debug("OpenLayersDriver.getOLSize in", state);
        let size = [state.extent[2] - state.extent[0], state.extent[3] - state.extent[1]];
        log.debug("OpenLayersDriver.getOLSize out", {size});
        return size;
    };

    /**
     * @brief Calculates size in D3 for a given OL size
     * @param ol_size, size (OL system)
     * @returns [width,height] the size in D3 pixels
     */
    this.from_ol_size_to_pixel = (ol_size) => {
        let state = this.get_initial_frame();
        let size = [(state.size[0] * ol_size[0]) / (state.extent[2] - state.extent[0]), (state.size[1] * ol_size[1]) / (state.extent[3] - state.extent[1])];
        log.debug("OpenLayersDriver.from_ol_size_to_pixel", {ol_size, size});
        return size;
    };

    /**
     * @brief Calculates coordinates in the OL map, from a given pixel on the screen.
     * @param ol, [x,y] absolute point in the OL map (in OpenLayer coordinates - meter near the equator)
     * @returns [x,y] the point in the D3 coordinate system (in pixels)
     */
    this.getPixelCoordinates = (ol) => {
        let state = this.get_initial_frame();
        if (state.extent[2] == state.extent[0] || state.extent[3] == state.extent[1]) {
            log.error("OpenLayersDriver.getPixelCoordinates division by 0 Error");
            return;
        }

        let pixel = [((ol[0] - state.extent[0]) * state.size[0]) / (state.extent[2] - state.extent[0]), ((state.extent[3] - ol[1]) * state.size[1]) / (state.extent[3] - state.extent[1])];

        log.debug("OpenLayersDriver.getPixelCoordinates", {ol, pixel});
        return pixel;
    };

    /**
     * create_gis_geonodes: create geonodes for geolocalisation feature
     * @param {object} gisState, contains map parameters: center, resolution, window size
     * @returns
     */
    this.create_gis_geonodes = (gisState) => {
        log.debug("create_gis_geonodes gisState ", {gisState});
        // create geonodes with corner bottom-left of the gis map

        let AT = getOLCoordinates2([0, gisState.size[1]], gisState.extent, gisState.size);
        let A = toLonLat(AT);
        log.debug("A, AT", {A, AT});

        // create geonodes with corner top-right of the gis map

        let BT = getOLCoordinates2([gisState.size[0], 0], gisState.extent, gisState.size);
        let B = toLonLat(BT);
        log.debug("B,BT ", {B, BT});

        let data = {};
        data.A = {X: 0, Y: gisState.size[1], E: A[0], N: A[1]};
        data.B = {X: gisState.size[0], Y: 0, E: B[0], N: B[1]};
        global.storage.selected_context.projection.import(data);
        global.storage.selected_context.projection.save();
        // do not show the geonodes
        // display map scale
        global.scene.map.grid.frame.draw();
    };

    /**
     * Compute the distance between two openlayers coordinates, taking into account the OL projection.
     * @param {Array} p1 the first [lon,lat] point
     * @param {Array} p2 the second [lon,lat] point
     * @return the distance in meters
     */
    this.get_distance = (p1, p2) => {
        log.debug("OpenLayersDriver.get_distance in ", {p1, p2});
        let distance = getDistance(p1, p2);
        log.debug("OpenLayersDriver.get_distance out ", {distance});
        return distance;
    };
};

export default OpenLayersDriver;
